import TwoFactorVerify from './TwoFactorVerify';
import OrgSelector from './OrgSelector';
/**
 * Estate Flow - Property Management Platform
 * Property Manager Login Component
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Building2, TrendingUp, ArrowLeft, Palmtree, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyManagerLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFAEmail, setTwoFAEmail] = useState("");
  const [twoFAUserId, setTwoFAUserId] = useState("");
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [orgMemberships, setOrgMemberships] = useState([]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password
      });

      if (response.data.requires_2fa) {
        setShow2FA(true);
        setTwoFAEmail(response.data.email);
        setTwoFAUserId(response.data.user_id);
        setLoading(false);
        return;
      }

      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('user_role', response.data.user?.role || 'property_manager');
        localStorage.setItem('user_email', email);
        toast.success('Login successful!');
        navigate('/property-manager/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  if (showOrgSelector) {
    return <OrgSelector memberships={orgMemberships} />;
  }

  if (show2FA) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center py-12 px-4">
        <TwoFactorVerify
          email={twoFAEmail}
          userId={twoFAUserId}
          onSuccess={(data) => {
            if (data.requires_membership_selection && data.memberships?.length > 1) {
              setOrgMemberships(data.memberships);
              setShowOrgSelector(true);
              return;
            }
            toast.success("Login successful!");
            setTimeout(() => { window.location.replace("/property-manager/dashboard"); }, 100);
          }}
          onBack={() => { setShow2FA(false); }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center space-y-4">
          <Button
            variant="ghost"
            className="absolute top-4 left-4"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center justify-center space-x-2 mb-2 pt-4">
            <Building2 className="h-10 w-10 text-purple-600" />
            <span className="text-3xl font-bold">Estate Flow</span>
          </div>
          <div className="flex items-center justify-center space-x-2 text-purple-600">
            <TrendingUp className="h-6 w-6" />
            <span className="font-semibold text-lg">Property Manager Portal</span>
          </div>
          <CardDescription>
            Manage your property portfolio and vacation rentals
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full bg-purple-600 hover:bg-purple-700"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <div className="text-right mt-2"><button type="button" className="text-sm text-purple-600 hover:underline" onClick={() => navigate('/forgot-password')}>Forgot Password?</button></div>
          </form>
          
          <div className="border-t pt-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Palmtree className="h-5 w-5 text-emerald-600" />
                <span className="font-semibold text-emerald-800">Vacation Rentals</span>
              </div>
              <p className="text-sm text-emerald-700 mb-3">
                Now available! Manage short-term rentals with channel sync and Book Direct & Save widget.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={() => navigate('/vacation-rentals')}
              >
                Learn More
              </Button>
            </div>
          </div>
          
          <div className="text-center text-sm text-gray-500">
            <p>Need an account?</p>
            <Button variant="link" onClick={() => navigate('/onboarding')}>
              Contact us to get started
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PropertyManagerLogin;
